import apiClient from './apiClient';
import {
  CreateReviewDto,
  UpdateReviewDto,
  AdminCreateReviewDto,
  ReviewDto,
  PublicReviewDto,
  AdminReviewDto,
  ReviewStatisticsDto,
  CanReviewResponseDto,
  ReviewTokenValidationDto,
  PublicReviewFilterDto,
  AdminReviewFilterDto,
} from '../types/review';
import { PaginatedResponse } from '../types/common';

// ============================================================================
// Public Review Operations (No auth required)
// ============================================================================

/**
 * Get paginated public reviews for display on homepage/reviews page
 */
export const getPublicReviews = async (
  filter: PublicReviewFilterDto
): Promise<PaginatedResponse<PublicReviewDto>> => {
  const params = new URLSearchParams();
  params.append('pageNumber', filter.pageNumber.toString());
  params.append('pageSize', filter.pageSize.toString());
  if (filter.minRating) params.append('minRating', filter.minRating.toString());
  if (filter.sortBy) params.append('sortBy', filter.sortBy);
  if (filter.sortDirection) params.append('sortDirection', filter.sortDirection);

  const response = await apiClient.get(`/reviews/public?${params.toString()}`);
  return response.data.data;
};

/**
 * Get public review statistics (average rating, distribution)
 */
export const getPublicStatistics = async (): Promise<ReviewStatisticsDto> => {
  const response = await apiClient.get('/reviews/public/statistics');
  return response.data.data;
};

/**
 * Validate a review token from email link
 */
export const validateToken = async (token: string): Promise<ReviewTokenValidationDto> => {
  const response = await apiClient.get(`/reviews/validate-token/${token}`);
  return response.data.data;
};

/**
 * Submit a review using a token (from email link, supports up to 5 photos)
 */
export const submitReviewWithToken = async (
  dto: CreateReviewDto,
  photos?: File[]
): Promise<ReviewDto> => {
  const formData = new FormData();

  if (dto.token) formData.append('token', dto.token);
  if (dto.orderId) formData.append('orderId', dto.orderId);
  formData.append('rating', dto.rating.toString());
  formData.append('reviewerName', dto.reviewerName);
  if (dto.comment) formData.append('comment', dto.comment);
  if (dto.email) formData.append('email', dto.email);
  if (dto.highlights) {
    dto.highlights.forEach((h) => formData.append('highlights', h));
  }
  if (dto.wouldOrderAgain) formData.append('wouldOrderAgain', dto.wouldOrderAgain);
  formData.append('agreeToPublish', dto.agreeToPublish.toString());
  if (photos && photos.length > 0) {
    photos.forEach((photo) => formData.append('photos', photo));
  }

  const response = await apiClient.post('/reviews', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
};

/**
 * Opt out from review request emails
 */
export const optOutFromEmails = async (token: string): Promise<boolean> => {
  const response = await apiClient.post(`/reviews/opt-out/${token}`);
  return response.data.data;
};

// ============================================================================
// Customer Review Operations (Auth required)
// ============================================================================

/**
 * Check if user can review an order
 */
export const canReviewOrder = async (orderId: string): Promise<CanReviewResponseDto> => {
  const response = await apiClient.get(`/reviews/can-review/${orderId}`);
  return response.data.data;
};

/**
 * Get user's review for a specific order
 */
export const getUserReviewForOrder = async (orderId: string): Promise<ReviewDto | null> => {
  const response = await apiClient.get(`/reviews/order/${orderId}`);
  return response.data.data;
};

/**
 * Get all reviews submitted by the current user
 */
export const getUserReviews = async (): Promise<ReviewDto[]> => {
  const response = await apiClient.get('/reviews/my-reviews');
  return response.data.data;
};

/**
 * Submit a review for an order (authenticated user, supports up to 5 photos)
 */
export const submitReview = async (
  dto: CreateReviewDto,
  photos?: File[]
): Promise<ReviewDto> => {
  const formData = new FormData();

  if (dto.orderId) formData.append('orderId', dto.orderId);
  if (dto.token) formData.append('token', dto.token);
  formData.append('rating', dto.rating.toString());
  formData.append('reviewerName', dto.reviewerName);
  if (dto.comment) formData.append('comment', dto.comment);
  if (dto.email) formData.append('email', dto.email);
  if (dto.highlights) {
    dto.highlights.forEach((h) => formData.append('highlights', h));
  }
  if (dto.wouldOrderAgain) formData.append('wouldOrderAgain', dto.wouldOrderAgain);
  formData.append('agreeToPublish', dto.agreeToPublish.toString());
  if (photos && photos.length > 0) {
    photos.forEach((photo) => formData.append('photos', photo));
  }

  const response = await apiClient.post('/reviews', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
};

// ============================================================================
// Admin Review Operations
// ============================================================================

/**
 * Get paginated reviews for admin management
 */
export const getAdminReviews = async (
  filter: AdminReviewFilterDto
): Promise<PaginatedResponse<AdminReviewDto>> => {
  const params = new URLSearchParams();
  params.append('pageNumber', filter.pageNumber.toString());
  params.append('pageSize', filter.pageSize.toString());
  if (filter.rating !== undefined) params.append('rating', filter.rating.toString());
  if (filter.isEnabled !== undefined) params.append('isEnabled', filter.isEnabled.toString());
  if (filter.searchTerm) params.append('searchTerm', filter.searchTerm);
  if (filter.fromDate) params.append('fromDate', filter.fromDate);
  if (filter.toDate) params.append('toDate', filter.toDate);
  if (filter.sortBy) params.append('sortBy', filter.sortBy);
  if (filter.sortDirection) params.append('sortDirection', filter.sortDirection);

  const response = await apiClient.get(`/admin/reviews?${params.toString()}`);
  return response.data.data;
};

/**
 * Get a single review by ID (admin)
 */
export const getAdminReviewById = async (reviewId: string): Promise<AdminReviewDto> => {
  const response = await apiClient.get(`/admin/reviews/${reviewId}`);
  return response.data.data;
};

/**
 * Update a review (admin)
 */
export const updateReview = async (
  reviewId: string,
  dto: UpdateReviewDto
): Promise<AdminReviewDto> => {
  const response = await apiClient.put(`/admin/reviews/${reviewId}`, dto);
  return response.data.data;
};

/**
 * Toggle review enabled status (admin)
 */
export const toggleReviewStatus = async (reviewId: string): Promise<boolean> => {
  const response = await apiClient.post(`/admin/reviews/${reviewId}/toggle-status`);
  return response.data.data;
};

/**
 * Get review statistics for admin dashboard
 */
export const getAdminStatistics = async (): Promise<ReviewStatisticsDto> => {
  const response = await apiClient.get('/admin/reviews/statistics');
  return response.data.data;
};

/**
 * Delete a review (admin)
 */
export const deleteReview = async (reviewId: string): Promise<boolean> => {
  const response = await apiClient.delete(`/admin/reviews/${reviewId}`);
  return response.data.data;
};

/**
 * Create a review as admin (standalone, no order required)
 */
export const adminCreateReview = async (dto: AdminCreateReviewDto): Promise<AdminReviewDto> => {
  const response = await apiClient.post('/admin/reviews', dto);
  return response.data.data;
};

/**
 * Send review request email to customer (admin)
 */
export const sendReviewRequestEmail = async (
  orderId: string,
  isReminder = false
): Promise<boolean> => {
  const response = await apiClient.post(`/admin/reviews/send-request/${orderId}`, null, {
    params: { isReminder },
  });
  return response.data.data;
};

// Export as default object for consistency with other services
const reviewService = {
  // Public
  getPublicReviews,
  getPublicStatistics,
  validateToken,
  submitReviewWithToken,
  optOutFromEmails,
  // Customer
  canReviewOrder,
  getUserReviewForOrder,
  getUserReviews,
  submitReview,
  // Admin
  getAdminReviews,
  getAdminReviewById,
  updateReview,
  deleteReview,
  adminCreateReview,
  toggleReviewStatus,
  getAdminStatistics,
  sendReviewRequestEmail,
};

export default reviewService;
